import { useEffect, useState } from 'react'
import { usersApi } from '../../api/users'
import { callsWs } from '../../ws/callsClient'
import { Avatar } from '../ui/Avatar'
import type { User } from '../../types'
import type { IncomingCall } from '../../stores/callStore'

interface Props {
  call: IncomingCall
  onAccept: (call: IncomingCall, caller: User) => void
  onReject: () => void
}

export function IncomingCallModal({ call, onAccept, onReject }: Props) {
  const [caller, setCaller] = useState<User | null>(null)

  useEffect(() => {
    usersApi.getProfile(call.callerId).then(setCaller).catch(() => {})
  }, [call.callerId])

  // Ring sound
  useEffect(() => {
    let stopped = false
    const ring = () => {
      if (stopped) return
      try {
        const ctx = new AudioContext()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.frequency.value = 440
        gain.gain.setValueAtTime(0.2, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8)
        osc.start(ctx.currentTime)
        osc.stop(ctx.currentTime + 0.8)
        ctx.close()
      } catch { /* blocked */ }
    }
    ring()
    const id = setInterval(ring, 2000)
    return () => { stopped = true; clearInterval(id) }
  }, [])

  const callerName = caller
    ? [caller.first_name, caller.last_name].filter(Boolean).join(' ') || caller.username
    : call.callerId

  const handleReject = () => {
    callsWs.send({ type: 'call.reject', room_id: call.roomId })
    onReject()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-12 sm:items-center bg-black/60">
      <div className="bg-bg-primary rounded-2xl shadow-2xl px-8 py-7 flex flex-col items-center gap-4 min-w-[280px] animate-fade-in">
        <Avatar name={callerName} src={caller?.avatar_url} size={72} />

        <div className="text-center">
          <p className="text-txt-primary font-semibold text-lg">{callerName}</p>
          <p className="text-txt-secondary text-sm mt-0.5">
            Входящий {call.callType === 'video' ? 'видео' : 'голосовой'} звонок...
          </p>
        </div>

        <div className="flex gap-6 mt-2">
          {/* Reject */}
          <button
            onClick={handleReject}
            className="flex flex-col items-center gap-1.5"
          >
            <div className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors shadow-lg">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
                <line x1="2" y1="22" x2="22" y2="2" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="text-txt-secondary text-xs">Отклонить</span>
          </button>

          {/* Accept */}
          <button
            onClick={() => caller && onAccept(call, caller)}
            disabled={!caller}
            className="flex flex-col items-center gap-1.5 disabled:opacity-50"
          >
            <div className="w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white transition-colors shadow-lg">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.48 2 2 0 0 1 3.6 2.28h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.72 17z"/>
              </svg>
            </div>
            <span className="text-txt-secondary text-xs">Принять</span>
          </button>
        </div>
      </div>
    </div>
  )
}
